import { useEffect, useRef, useState } from "react";

let apiPromise;
const loadApi = () => {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return apiPromise;
};

function NativeVideo({ src, poster, onFail }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    const events = ["touchstart", "touchend", "click", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, tryPlay, { passive: true }));
    tryPlay();
    return () => events.forEach((ev) => window.removeEventListener(ev, tryPlay));
  }, []);

  return (
    <video
      ref={ref}
      className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-0"}`}
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      onPlaying={() => setPlaying(true)}
      onError={onFail}
      data-testid="hero-native-video"
      data-playing={playing}
      aria-hidden="true"
    />
  );
}

function YouTubeVideo({ videoId, start, end }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let loopTimer;
    let retryTimer;
    let cancelled = false;

    const tryPlay = () => {
      const p = playerRef.current;
      if (!p?.playVideo) return;
      try {
        p.mute();
        if (p.getPlayerState?.() !== 1) p.playVideo();
      } catch (_) {}
    };
    const gestureEvents = ["touchstart", "touchend", "click", "scroll", "keydown"];
    gestureEvents.forEach((ev) => window.addEventListener(ev, tryPlay, { passive: true }));
    const onVisible = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVisible);

    loadApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId,
        host: "https://www.youtube-nocookie.com",
        playerVars: { autoplay: 1, mute: 1, controls: 0, playsinline: 1, rel: 0, modestbranding: 1, iv_load_policy: 3, disablekb: 1, fs: 0, start, end, loop: 1, playlist: videoId, enablejsapi: 1, origin: window.location.origin },
        events: {
          onReady: (e) => {
            e.target.mute();
            e.target.seekTo(start, true);
            e.target.playVideo();
            let attempts = 0;
            retryTimer = setInterval(() => {
              attempts += 1;
              if (attempts > 20 || e.target.getPlayerState?.() === 1) return clearInterval(retryTimer);
              tryPlay();
            }, 1500);
            loopTimer = setInterval(() => {
              const p = playerRef.current;
              if (!p?.getCurrentTime) return;
              const t = p.getCurrentTime();
              if (t >= end - 0.3 || t < start - 1) p.seekTo(start, true);
            }, 500);
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) setPlaying(true);
            if (e.data === YT.PlayerState.ENDED || e.data === YT.PlayerState.PAUSED) {
              e.target.seekTo(start, true);
              e.target.playVideo();
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      clearInterval(loopTimer);
      clearInterval(retryTimer);
      gestureEvents.forEach((ev) => window.removeEventListener(ev, tryPlay));
      document.removeEventListener("visibilitychange", onVisible);
      playerRef.current?.destroy?.();
    };
  }, [videoId, start, end]);

  return (
    <div className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-0"}`} data-testid="hero-video" data-start={start} data-end={end} data-playing={playing}>
      <div ref={hostRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hero-video-frame" />
    </div>
  );
}

// Prefers a self-hosted mp4 (plays on every phone); falls back to YouTube when no file is uploaded.
export default function HeroVideo({ videoId, start, end, mp4, poster }) {
  const [mode, setMode] = useState("checking");
  useEffect(() => {
    if (!mp4) return setMode("youtube");
    fetch(mp4, { method: "HEAD" })
      .then((r) => setMode(r.ok && /video/i.test(r.headers.get("content-type") || "") ? "native" : "youtube"))
      .catch(() => setMode("youtube"));
  }, [mp4]);

  if (mode === "checking") return null;
  if (mode === "native") return <NativeVideo src={mp4} poster={poster} onFail={() => setMode("youtube")} />;
  return <YouTubeVideo videoId={videoId} start={start} end={end} />;
}
